<template>
  <div
    data-testid="tm-results-report-v2__view"
    class="tm-results-report-v2"
  >
    <!-- Toolbar: Date Filter -->
    <div class="report-v2__toolbar" data-testid="tm-results-report-v2__toolbar">
      <div class="report-v2__toolbar-left">
        <span class="report-v2__as-at-label">As at</span>

        <div class="report-v2__date-nav">
          <button
            type="button"
            class="report-v2__date-nav-btn"
            data-testid="tm-results-report-v2__prev-date-btn"
            :disabled="isPrevDateDisabled"
            title="Previous day"
            aria-label="Previous day"
            @click="goToPreviousDate"
          >
            <v-icon :size="15">mdi-chevron-left</v-icon>
          </button>

        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          offset-y
          min-width="290"
          data-testid="tm-results-report-v2__date-menu"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              class="report-v2__date-btn"
              v-bind="attrs"
              v-on="on"
              data-testid="tm-results-report-v2__date-btn"
            >
              <v-icon small class="report-v2__date-btn-icon">mdi-calendar</v-icon>
              <span :class="selectedDate ? 'report-v2__date-btn-text--active' : 'report-v2__date-btn-text--placeholder'">
                {{ selectedDate ? $nova.formatDate(selectedDate) : 'Select Date' }}
              </span>
            </button>
          </template>

          <v-date-picker
            v-model="selectedDate"
            no-title
            :min="minDateAllowed"
            :max="maxDateAllowed"
            :allowed-dates="isDateAllowed"
            :events="availableReportDates"
            event-color="error"
            class="report-v2__date-picker"
            @input="onDateSelected"
            color="primary"
          />
        </v-menu>

          <button
            type="button"
            class="report-v2__date-nav-btn"
            data-testid="tm-results-report-v2__next-date-btn"
            :disabled="isNextDateDisabled"
            title="Next day"
            @click="goToNextDate"
          >
            <v-icon :size="15">mdi-chevron-right</v-icon>
          </button>
        </div>

        <v-progress-circular
          v-if="loading || availabilityLoading"
          indeterminate
          size="18"
          width="2"
          color="primary"
          class="ml-2"
        />
      </div>

      <v-spacer />

      <DownloadExcel
        button-text="Generate Excel"
        button-color="white"
        test-id-prefix="tm-results-report-v2__download-excel"
        :download-history="filteredDownloadHistory"
        :loading-history="loadingHistory"
        :current-user-id="$store.state.user ? String($store.state.user.id || $store.state.user._id || '') : ''"
        :compact="true"
        :total-count="reportExportTotalCount"
        @generate="handleGenerateExcel"
        @refresh="$emit('refresh-download-history')"
        @download="$emit('download-history-file', $event)"
      />
    </div>

    <!-- Loading State (initial load, no data yet) -->
    <div
      v-if="(loading || availabilityLoading) && !reportRecord"
      class="report-v2__loading"
      data-testid="tm-results-report-v2__loading"
    >
      <img src="/img/loader_new.svg" alt="Loading" class="report-v2__loading-img" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!reportRecord"
      class="text-center grey--text title pa-6"
      data-testid="tm-results-report-v2__empty"
    >
      No report data available
    </div>

    <!-- Report Tables -->
    <div
      v-else
      class="report-v2__content"
      data-testid="tm-results-report-v2__content"
    >
      <div
        v-for="tableName in TABLE_NAMES"
        :key="tableName"
        class="report-v2__table-section"
        :data-testid="'tm-results-report-v2__table-' + tableName.replace(/\s+/g, '-').toLowerCase()"
      >
        <template v-if="reportRecord.reports && reportRecord.reports[tableName]">
          <div class="report-v2__card">
          <table class="report-v2__table">
            <!-- Column proportions: first col wide, rest equal -->
            <colgroup>
              <col style="width: 65%" />
              <col
                v-for="(header, ci) in reportRecord.reports[tableName].headers.slice(1)"
                :key="'col-' + ci"
              />
            </colgroup>

            <!-- Table Title Row + Column Headers -->
            <thead>
              <!-- Card title row -->
              <tr class="report-v2__title-row">
                <td
                  :colspan="reportRecord.reports[tableName].headers.length"
                  class="report-v2__title-cell"
                >
                  {{ tableName }}
                </td>
              </tr>
              <!-- Column header row -->
              <tr class="report-v2__header-row">
                <th
                  v-for="(header, hi) in reportRecord.reports[tableName].headers"
                  :key="header"
                  class="report-v2__th"
                  :class="{
                    'report-v2__th--first': hi === 0,
                    'report-v2__th--border-right': hi < reportRecord.reports[tableName].headers.length - 1
                  }"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>

            <!-- Table Body -->
            <tbody>
              <template
                v-for="(renderedItem, ri) in buildRenderedItems(reportRecord.reports[tableName].lineItems, tableName)"
              >
                <!-- Section Header Row (accordion trigger) -->
                <tr
                  v-if="renderedItem.type === 'section'"
                  :key="'section-' + tableName + '-' + ri"
                  class="report-v2__section-row"
                  data-testid="tm-results-report-v2__section-row"
                  @click="toggleSection(tableName, renderedItem.name)"
                >
                  <td
                    :colspan="reportRecord.reports[tableName].headers.length"
                    class="report-v2__section-cell"
                  >
                    <v-icon small class="report-v2__section-chevron">
                      {{ isSectionCollapsed(tableName, renderedItem.name) ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                    </v-icon>
                    <strong>{{ renderedItem.name }}</strong>
                  </td>
                </tr>

                <!-- Data Row -->
                <tr
                  v-else
                  :key="'row-' + tableName + '-' + ri"
                  class="report-v2__row"
                  :style="rowStyle(renderedItem.item['css-style'])"
                  data-testid="tm-results-report-v2__data-row"
                >
                  <td
                    v-for="(header, hi) in reportRecord.reports[tableName].headers"
                    :key="header"
                    class="report-v2__td"
                    :class="{
                      'report-v2__td--null': renderedItem.item.row[header] === null || renderedItem.item.row[header] === undefined,
                      'report-v2__td--border-right': hi < reportRecord.reports[tableName].headers.length - 1,
                      'report-v2__td--center': hi > 0
                    }"
                    :style="hi === 0 ? firstCellStyle(renderedItem.item['css-style'], renderedItem.inSection) : {}"
                  >
                    <div class="d-flex justify-space-between align-center">
                      {{ formatCell(renderedItem.item.row[header]) }}

                    <!-- Pending Payouts drill-down button -->
                    <base-button
                      v-if="hi === 0
                        && tableName === TM_REPORT_TABLES.CLOSING_BALANCE_SUMMARY
                        && renderedItem.item.row[header] === TM_PENDING_PAYOUTS.REPORT_ROW_LABEL
                        && hasPendingPayoutsFeature"
                      secondary
                      small
                      class="report-v2__drilldown-btn"
                      :title="'View ' + renderedItem.item.row[header]"
                      data-testid="tm-results-report-v2__pending-payouts-btn"
                      @click.stop="openPendingPayouts"
                    >
                      <v-icon x-small>mdi-eye-outline</v-icon>
                    </base-button>

                    <!-- Report line drill-down eye icon -->
                    <base-button
                      v-if="hi === 0 && isRowDrilldownEligible(renderedItem.item.row[header])"
                      secondary
                      small
                      class="report-v2__drilldown-btn"
                      :title="'View ' + renderedItem.item.row[header]"
                      data-testid="tm-results-report-v2__drilldown-btn"
                      @click.stop="openDrilldown(tableName, renderedItem)"
                    >
                      <v-icon x-small>mdi-eye-outline</v-icon>
                    </base-button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * TMResultsReportV2 Component
 *
 * Dedicated Report tab renderer for Transaction Matching V2 results.
 * - Single date filter ("As at") to filter by report_date
 * - Two static tables: "Bank Balance Movement" and "Closing Balance Summary"
 * - Sections act as accordion rows (bold header + chevron toggle)
 * - Row-level css-style applied as inline styles from API data
 *
 * @component
 */

import { mapActions, mapGetters } from 'vuex';
import DownloadExcel from '../TransationMatching/downloadExcel.vue';
import { isDrilldownEligible, resolveReportTableKey } from '../../../../utils/tmReportDrilldownUtils.js';
import { TM_PENDING_PAYOUTS, TM_REPORT_TABLES } from '../../../../utils/constants.js';
import {
  getRowSummaryAmount,
  serializeCollapsedSections,
  deserializeCollapsedSections,
} from './tmReportDrilldownViewUtils.js';

export default {
  name: 'TMResultsReportV2',

  components: {
    DownloadExcel,
  },

  props: {
    /** The collection name for the Report annexure. */
    collection: {
      type: String,
      required: true
    },
    /** Analysis details object containing bpid, _id, filecount, etc. */
    analysisDetails: {
      type: Object,
      required: true,
      default: () => ({})
    },
    /** Single V2 rundetail object. */
    runDetails: {
      type: Object,
      required: true,
      default: () => ({})
    },
    /** File configuration info containing column types and mappings. */
    fileConfigInfo: {
      type: Object,
      default: () => ({})
    },
    /** Array of download history files for DownloadExcel dropdown. */
    downloadHistory: {
      type: Array,
      default: () => []
    },
    /** Loading state for download history. */
    loadingHistory: {
      type: Boolean,
      default: false
    },
    /** Original annexure tab name (e.g. "Report"). */
    tabName: {
      type: String,
      default: ''
    },
    /** Result types from formData (passed from parent). Used to gate the Pending Payouts feature. */
    resultTypes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      TABLE_NAMES: TM_REPORT_TABLES.ORDERED,
      TM_PENDING_PAYOUTS,
      TM_REPORT_TABLES,
      loading: false,
      availabilityLoading: false,
      availabilityError: null,
      availableReportDates: [],
      dateMenu: false,
      selectedDate: null,
      /** The first record returned by the API (reports data lives here) */
      reportRecord: null,
      /**
       * Tracks collapsed sections per table.
       * Shape: { [tableName]: Set<sectionName> }
       */
      collapsedSections: {},
    };
  },

  computed: {
    ...mapGetters('transactionMatching', ['formData', 'reportSnapshot']),

    drillDownFields() {
      return this.formData?.drillDownFields || [];
    },

    /**
     * Lookup the collection for the Pending Payouts annexure entry in runDetails.
     * This is distinct from the Report tab's collection.
     */
    pendingPayoutsCollection() {
      const annexure = this.runDetails?.annexure;
      if (!annexure || !Array.isArray(annexure)) return null;
      const entry = annexure.find(item =>
        (item.name || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim().includes('pending payout')
      );
      return entry?.collection || null;
    },

    /**
     * Whether the "Pending Payouts" result type is enabled for this analysis.
     *
     * Primary: analysisDetails.results (per-analysis strings from API, e.g. ['Pending Payouts']).
     * This is always available once the analysis is loaded, regardless of whether the user
     * has visited the TM list page (which is the only place that populates formData.results).
     *
     * Fallback: resultTypes prop or formData.results for backward compatibility.
     */
    hasPendingPayoutsFeature() {
      const apiResults = this.analysisDetails?.results;
      if (Array.isArray(apiResults) && apiResults.length > 0) {
        return apiResults.some(r => {
          const name = String(typeof r === 'object' ? (r._id || r.name || '') : (r || ''));
          return name.toLowerCase().replace(/[\s_-]+/g, '').includes('pendingpayout');
        });
      }
      // Fallback: resultTypes prop (objects with _id) or store's formData.results
      const types = this.resultTypes.length ? this.resultTypes : (this.formData?.results || []);
      return types.some(r => {
        const name = String(typeof r === 'object' ? (r._id || r.name || '') : (r || ''));
        return name.toLowerCase().replace(/[\s_-]+/g, '').includes('pendingpayout');
      });
    },

    /** Convert selected date (YYYY-MM-DD) to dd-mm-yyyy for API */
    reportDateFormatted() {
      if (!this.selectedDate) return null;
      const [y, m, d] = this.selectedDate.split('-');
      return `${d}-${m}-${y}`;
    },

    /** Filter download history to only show entries matching this tab. */
    filteredDownloadHistory() {
      if (!this.tabName) return this.downloadHistory;
      return this.downloadHistory.filter(
        file => !Array.isArray(file.tables) || file.tables.length === 0 || file.tables.includes(this.tabName)
      );
    },

    /** Disable Generate Excel when the report tab has no loaded data. */
    reportExportTotalCount() {
      if (this.loading || this.availabilityLoading) return 0;
      return this.reportRecord ? 1 : 0;
    },

    /**
     * Minimum date allowed in the date picker.
     * Uses startdate from analysisDetails.
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    minDateAllowed() {
      const startdate = this.analysisDetails?.startdate;
      return startdate ? this.$nova.formatDateISO(startdate) : null;
    },

    /**
     * Maximum date allowed in the date picker.
     * Uses enddate from analysisDetails.
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    maxDateAllowed() {
      const enddate = this.analysisDetails?.enddate;
      if (!enddate) return this.$nova.formatDateISO(new Date());
      return this.$nova.formatDateISO(enddate);
    },

    isPrevDateDisabled() {
      if (!this.selectedDate || !this.minDateAllowed) return true;
      return this.selectedDate <= this.minDateAllowed;
    },

    isNextDateDisabled() {
      if (!this.selectedDate || !this.maxDateAllowed) return true;
      return this.selectedDate >= this.maxDateAllowed;
    },

    /**
     * Ownership context required by the annexure-report-dates API.
     * pid is the financial-close cycle (analysisDetails.pid).
     * projectId is the bot project (analysisDetails.bpid).
     * verificationId is runDetails.verification.
     */
    annexureOwnershipContext() {
      const projectId = this.analysisDetails?.bpid;
      const verificationId = this.runDetails?.verification;
      const pid = this.analysisDetails?.pid || this.$route.params.id || '';

      if (!pid || !projectId || !verificationId) return null;

      return {
        pid,
        projectId,
        verificationId,
      };
    },

    /** Build the filter payload for the API using the correct schema. */
    dateFilterPayload() {
      if (!this.selectedDate) return { groups: [], group_connectors: [] };
      return {
        groups: [
          {
            connector: 'AND',
            conditions: [
              { column: 'report_date', operator: 'eq', value: this.selectedDate }
            ]
          }
        ],
        group_connectors: []
      };
    }
  },

  mounted() {
    this.initializeReport();
  },

  methods: {
    ...mapActions('transactionMatching', ['fetchAnnexureReportDates', 'fetchAnnexureData']),

    async initializeReport() {
      const snapshot = this.$store.state.transactionMatching?.reportSnapshot;
      if (snapshot?.reportRecord) {
        this.hydrateFromSnapshot(snapshot);
        return;
      }
      await this.loadAvailableReportDatesAndSelectDefault();
      await this.loadData();
    },

    hydrateFromSnapshot(snapshot) {
      if (snapshot.selectedDate) {
        this.selectedDate = this.clampDateToAllowedRange(snapshot.selectedDate);
      }
      if (snapshot.reportRecord) {
        this.reportRecord = snapshot.reportRecord;
      }
      if (snapshot.collapsedSections) {
        this.collapsedSections = deserializeCollapsedSections(snapshot.collapsedSections);
      }
    },

    isRowDrilldownEligible(description) {
      return isDrilldownEligible(description, this.drillDownFields);
    },

    openDrilldown(tableName, renderedItem) {
      const table = this.reportRecord?.reports?.[tableName];
      if (!table) return;

      const headers = table.headers || [];
      const descriptionHeader = headers[0];
      const description = renderedItem.item.row[descriptionHeader];
      const reportTable = resolveReportTableKey(tableName);

      this.$emit('open-drilldown', {
        field: description,
        tableName,
        reportTable,
        summaryAmount: getRowSummaryAmount(renderedItem.item.row, headers),
        reportDate: this.selectedDate,
        selectedDate: this.selectedDate,
        reportRecord: this.reportRecord,
        collapsedSections: serializeCollapsedSections(this.collapsedSections),
      });
    },

    openPendingPayouts() {
      this.$emit('open-pending-payouts', {
        collection: this.pendingPayoutsCollection,
        reportDate: this.reportDateFormatted,
        selectedDate: this.selectedDate,
        reportRecord: this.reportRecord,
        collapsedSections: serializeCollapsedSections(this.collapsedSections),
      });
    },

    getYesterdayIsoDate() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return this.$nova.formatDateISO(yesterday);
    },

    /**
     * Clamp a date to the analysis start/end range.
     * @param {string|null} date - YYYY-MM-DD
     * @returns {string|null}
     */
    clampDateToAllowedRange(date) {
      if (!date) return null;

      let clamped = date;
      if (this.minDateAllowed && clamped < this.minDateAllowed) {
        clamped = this.minDateAllowed;
      }
      if (this.maxDateAllowed && clamped > this.maxDateAllowed) {
        clamped = this.maxDateAllowed;
      }
      return clamped;
    },

    /** Default selected date: yesterday, clamped to the allowed analysis period. */
    getDefaultSelectedDate() {
      return this.clampDateToAllowedRange(this.getYesterdayIsoDate());
    },

    /**
     * Allow any date within the analysis start/end range.
     * availableReportDates is used only for event dots, not selection.
     * @param {string} date - YYYY-MM-DD
     * @returns {boolean}
     */
    isDateAllowed(date) {
      if (this.minDateAllowed && date < this.minDateAllowed) return false;
      if (this.maxDateAllowed && date > this.maxDateAllowed) return false;
      return true;
    },

    async loadAvailableReportDatesAndSelectDefault() {
      this.availabilityLoading = true;
      this.availabilityError = null;
      this.availableReportDates = [];

      const ownership = this.annexureOwnershipContext;
      if (!this.collection || !ownership) {
        this.availabilityError = 'missing_context';
        this.selectedDate = this.getDefaultSelectedDate();
        this.availabilityLoading = false;
        return;
      }

      try {
        const dates = await this.fetchAnnexureReportDates({
          collection: this.collection,
          ...ownership,
        });
        this.availableReportDates = Array.isArray(dates) ? dates : [];
        this.selectedDate = this.availableReportDates.length > 0
          ? this.clampDateToAllowedRange(this.availableReportDates[0])
          : this.getDefaultSelectedDate();
      } catch (error) {
        console.error('[TMResultsReportV2]: loadAvailableReportDatesAndSelectDefault error', error);
        this.availabilityError = error?.response?.data?.message || error?.message || 'Failed to load available report dates';
        this.selectedDate = this.getDefaultSelectedDate();
      } finally {
        this.availabilityLoading = false;
      }
    },

    shiftSelectedDate(dayOffset) {
      if (!this.selectedDate) return;

      const [year, month, day] = this.selectedDate.split('-').map(Number);
      const nextDate = new Date(year, month - 1, day);
      nextDate.setDate(nextDate.getDate() + dayOffset);
      this.selectedDate = this.$nova.formatDateISO(nextDate);
      this.loadData();
    },

    goToPreviousDate() {
      if (this.isPrevDateDisabled) return;
      this.shiftSelectedDate(-1);
    },

    goToNextDate() {
      if (this.isNextDateDisabled) return;
      this.shiftSelectedDate(1);
    },

    /** Handle Generate Excel button click. */
    handleGenerateExcel() {
      const dataFilters = this.selectedDate
        ? JSON.stringify([{ $match: { report_date: { $eq: this.selectedDate } } }])
        : null;
      this.$emit('generate-result', {
        tables: this.tabName ? [this.tabName] : [],
        collection: this.collection || null,
        dataFilters
      });
    },

    /** Fetch report data from the annexure API. */
    async loadData() {
      if (!this.collection) {
        console.warn('[TMResultsReportV2]: No collection provided');
        return;
      }

      this.loading = true;

      try {
        const payload = {
          page: this.collection,
          filters: this.dateFilterPayload,
          sort: [{ field: 'report_date', direction: 'desc' }],
          pagination: { page: 1, limit: 50 },
          version: 'v2'
        };

        if (this.analysisDetails?.client) {
          payload.client = this.analysisDetails.client;
        }

        const data = await this.fetchAnnexureData({
          collection: this.collection,
          ...payload
        });

        const records = data.records || [];
        this.reportRecord = records[0] || null;
      } catch (error) {
        console.error('[TMResultsReportV2]: loadData error', error);
        this.$store.commit(
          'sbError',
          error?.response?.data?.message || error?.message || 'Failed to load report data',
          { root: true }
        );
        this.reportRecord = null;
      } finally {
        this.loading = false;
      }
    },

    /** Called when user picks a date from the picker. */
    onDateSelected() {
      this.dateMenu = false;
      this.loadData();
    },

    /** Clear the selected date and reload without a date filter. */
    clearDate() {
      this.selectedDate = null;
      this.loadData();
    },

    /**
     * Build flat list of rendered items for the table body.
     * Inserts section headers and tracks whether each row is inside a section.
     * Collapsed sections have their data rows omitted.
     *
     * @param {Array} lineItems
     * @param {string} tableName
     * @returns {Array<{type:'section', name:string} | {type:'row', item:Object, inSection:boolean}>}
     */
    buildRenderedItems(lineItems, tableName) {
      if (!Array.isArray(lineItems)) return [];

      const rendered = [];
      let currentSection = null;

      for (const item of lineItems) {
        const section = item.section || null;

        // Insert section header when section changes
        if (section !== currentSection) {
          if (section) {
            rendered.push({ type: 'section', name: section });
          }
          currentSection = section;
        }

        // Skip rows inside collapsed sections
        if (section && this.isSectionCollapsed(tableName, section)) continue;

        rendered.push({ type: 'row', item, inSection: !!section });
      }

      return rendered;
    },

    /**
     * Return true when the given section is currently collapsed.
     * @param {string} tableName
     * @param {string} sectionName
     * @returns {boolean}
     */
    isSectionCollapsed(tableName, sectionName) {
      return !!(this.collapsedSections[tableName]?.has(sectionName));
    },

    /**
     * Toggle collapsed/expanded state of a section.
     * @param {string} tableName
     * @param {string} sectionName
     */
    toggleSection(tableName, sectionName) {
      if (!this.collapsedSections[tableName]) {
        this.$set(this.collapsedSections, tableName, new Set());
      }
      
      const sections = this.collapsedSections[tableName];
      if (sections.has(sectionName)) {
        sections.delete(sectionName);
      } else {
        sections.add(sectionName);
      }
      
      // Force reactivity by replacing the Set
      this.$set(this.collapsedSections, tableName, new Set(sections));
    },

    /**
     * Format a cell value for display.
     * null/undefined → em-dash; numbers → locale-formatted.
     * @param {*} value
     * @returns {string}
     */
    formatCell(value) {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'number') {
        return this.$nova?.formatNumber
          ? this.$nova.formatNumber(value)
          : value.toLocaleString();
      }
      return String(value);
    },

    /**
     * Filter out padding properties from css-style for <tr> element.
     * Only inheritable properties like font-weight, color apply to rows.
     * @param {Object|null} cssStyle
     * @returns {Object}
     */
    rowStyle(cssStyle) {
      if (!cssStyle) return {};
      // eslint-disable-next-line no-unused-vars
      const { 'padding-left': _pl, 'padding-right': _pr, padding: _p, ...rest } = cssStyle;
      return rest;
    },

    /**
     * Style for the first <td> (Description column).
     * Applies section indent (1.5rem base) + any additional padding from css-style.
     * @param {Object|null} cssStyle
     * @param {boolean} inSection
     * @returns {Object}
     */
    firstCellStyle(cssStyle, inSection) {
      if (!inSection && !cssStyle?.['padding-left']) return {};

      const result = {};
      const apiPadding = cssStyle?.['padding-left'];
      const sectionBase = '1.5rem';

      if (inSection && apiPadding) {
        result['padding-left'] = `calc(${sectionBase} + ${apiPadding})`;
      } else if (inSection) {
        result['padding-left'] = sectionBase;
      } else if (apiPadding) {
        result['padding-left'] = apiPadding;
      }

      return result;
    }
  },

  beforeDestroy() {
    this.reportRecord = null;
    this.collapsedSections = {};
    this.selectedDate = null;
  }
};
</script>

<style scoped lang="scss">
@import './TMResultsReportV2.scss';
</style>
